import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { RemoveUserDocument } from 'src/graphql/actions/user/removeUser.mutation';
import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { ClientError, getApiError } from 'src/utils/errors';

import { useUsers } from '../model';

const RemoveDialog = () => {
  const {
    ui: { removeDialogOpen, userId, closeRemoveDialog },
  } = useUsers();
  const [removeUser, { loading }] = useMutation(RemoveUserDocument, { refetchQueries: [UsersDocument] });

  const onRemoveUser = async () => {
    if (!userId) return;

    try {
      const { data } = await removeUser({
        variables: {
          userId,
        },
      });
      if (data?.removeUser?.success) {
        toast.success('Member deleted');
        closeRemoveDialog();
      } else {
        throw new ClientError(data?.removeUser?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (!open) closeRemoveDialog();
  };

  return (
    <AlertDialog open={removeDialogOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to delete this member?</AlertDialogTitle>
          <AlertDialogDescription>
            This member will immediately lose access to your organization and all products they&apos;ve been assigned
            to.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" size="sm" onClick={closeRemoveDialog}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onRemoveUser} isLoading={loading} size="sm">
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveDialog;
